import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, Menu } from '@percent/lemonade'
import {
  type CausesPortalOpportunityDto,
  OpportunityStatus
} from '@percent/cause-dashboard/services/volunteering/volunteeringService.types'
import { useVolunteeringActions } from './useVolunteeringActions'
import { useCanManageOpportunity } from './useCanManageOpportunity'

export type ActionsMenuVolunteeringProps = Readonly<{
  opportunity: CausesPortalOpportunityDto
}>

export function ActionsMenuVolunteering({ opportunity }: ActionsMenuVolunteeringProps) {
  const { t } = useTranslation()
  const canManageOpportunity = useCanManageOpportunity({ opportunity })

  const items = useMemo(
    () => [
      ...(opportunity.status === OpportunityStatus.ACTIVE
        ? [
            {
              key: 'cancel opportunity',
              label: t('volunteering.actionsMenu.button.cancelOpportunity')
            }
          ]
        : []),
      ...(opportunity.status !== OpportunityStatus.CANCELLED && canManageOpportunity
        ? [
            {
              key: 'edit opportunity',
              label: t('volunteering.actionsMenu.button.editOpportunity')
            }
          ]
        : []),
      ...(opportunity.status !== OpportunityStatus.CANCELLED
        ? [
            {
              key: 'edit attendance',
              label: t('volunteering.actionsMenu.button.editAttendance')
            }
          ]
        : []),
      ...(opportunity.status !== OpportunityStatus.CANCELLED && opportunity.participants.count > 0
        ? [
            {
              key: 'export attendees',
              label: t('volunteering.actionsMenu.button.exportAttendees')
            }
          ]
        : [])
    ],
    [opportunity.status, t, canManageOpportunity, opportunity.participants.count]
  )

  const { editAttendance, exportAttendees, cancelOpportunity, editOpportunity } = useVolunteeringActions({
    opportunityId: opportunity.id,
    opportunityName: opportunity.name
  })

  const onSelect = (key: string) => {
    switch (key) {
      case 'edit opportunity':
        editOpportunity()
        break
      case 'edit attendance':
        editAttendance()
        break
      case 'export attendees':
        exportAttendees()
        break
      case 'cancel opportunity':
        cancelOpportunity()
        break
      default:
        break
    }
  }

  return <Menu title={<Icon name="menu-vertical" size={6} />} onSelect={onSelect} sections={[{ items }]} />
}
